/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import * as _ from 'lodash';
import { FormattedMessage } from 'react-intl';

import ShareCardHeader from './ShareCardHeader';
import {
  AppNavCancelButton,
  AppNavSubmitButton,
  AppNavDoneButton,
  AppNavCloseIconButton,
} from '../../../components/appNav/appNavBits/AppNavButtons';

import { Grid } from '../../../components/Grid';
import DynamicFontSize from '../../../components/DynamicFontSize';
import AppNavInputTitle from '../../../components/appNav/appNavBits/AppNavInputTitle';
import { I18nImageType } from '../../../../models/GroupMetadata';
import { I18nImage } from '../../../components/I18nTranslation';

const LeftControl = ({ onCancel, color, isDisabled }) => {
  if (onCancel) {
    return <AppNavCancelButton onClick={onCancel} color={color} />;
  }

  return <div />;
};

const RightControl = ({ onSubmit, onClose, onDone, color, isDisabled }) => {

  if (onSubmit) {
    return <AppNavSubmitButton onClick={onSubmit} isDisabled={isDisabled} />;
  }

  if (onDone) {
    return <AppNavDoneButton onClick={onDone} color={color} />;
  }

  if (onClose) {
    return <AppNavCloseIconButton onClick={onClose} color={color} style={{ right: -12 }} />;
  }

  return <div />;
};

interface ShareCardHeaderTitleProps {
  cssColor: string;
  iconClass?: string;
  lightIcon?: I18nImageType;
  title: any;
  onClose?(): void;
  onCancel?(): void;
  onSubmit?(): void;
  onDone?(data): void;
  isSubmitDisabled: boolean;
  children: any;
  isGroupAccessible: boolean;
}

const ShareCardHeaderTitle = (props: ShareCardHeaderTitleProps) => {
  const { children, cssColor, onDone, iconClass, lightIcon, onClose, onCancel, onSubmit, isSubmitDisabled, isGroupAccessible } = props;
  const textColor = 'white';

  return (
    <ShareCardHeader style={{ backgroundColor: cssColor, minHeight: '100px' }}>
      <nav style={{ height: 56, marginLeft: 16, marginRight: 16, display: 'flex', justifyContent: 'space-between' }}>
        <LeftControl onCancel={onCancel} color={textColor} isDisabled={false} />

        <RightControl onClose={onClose} onDone={onDone} onSubmit={onSubmit} color={textColor} isDisabled={isSubmitDisabled} />
      </nav>

      <Grid alignItems="center" style={{ paddingBottom: 18 }}>
        <div className="sharecard-title">
          {children}
        </div>

        {!!lightIcon &&
          <div className={`sharecard-icon ${iconClass}`}>
            <I18nImage data={lightIcon} />
          </div>
        }
      </Grid>

      {
        isGroupAccessible && (
          <div className="sharecard-groupAccessible" style={{ backgroundColor: cssColor }}>
            <FormattedMessage id="shareCard.accessibleByGroup.message.text" />
          </div>
        )
      }
    </ShareCardHeader>
  );
};

interface Props {
  cssColor: string;
  iconClass?: string;
  lightIcon?: I18nImageType;
  title: any;
  onClose?(): void;
  onCancel?(): void;
  onSubmit?(): void;
  onDone?(data): void;
  isSubmitDisabled: boolean;
  isGroupAccessible: boolean;
}

export const ReadOnlyShareCardHeaderTitle = (props: Props) => {
  const { title } = props;

  return (
    <ShareCardHeaderTitle {...props}>
      <DynamicFontSize
        text={title}
        minFontClass="font-headline"
        maxFontClass="font-title-2"
      />
    </ShareCardHeaderTitle>
  );
};

interface EditableProps {
  cssColor: string;
  iconClass: string;
  lightIcon: I18nImageType;
  title: string;

  cancelTitleChange(): void;
  submit(data): void;
  onEditTitle(value: boolean, title: string): void;
  isEditingTitle: boolean;

  customTitle: string;
  isEditable: boolean;
  isGroupAccessible: boolean;
}

export class EditableShareCardHeaderTitle extends React.Component<EditableProps, {}> {

  updateTitle = (event) => {
    const newTitle = event.target.value;
    const title = _.size(newTitle) <= 35 ? newTitle : this.props.title;
    this.props.onEditTitle(true, title);
  }

  onFocus = () => {
    this.props.onEditTitle(true, this.props.title);
  }

  finishEditingTitle = () => {
    if (this.isInvalid()) { return; }
    this.props.onEditTitle(false, this.props.title);
  }

  cancelTitleChange = () => {
    this.props.onEditTitle(false, this.props.title);
    this.props.cancelTitleChange();
  }

  isInvalid = () => {
    return !this.props.customTitle;
  }

  render() {
    const { submit, isEditingTitle, title, isEditable } = this.props;

    if (!isEditable) {
      return (
        <ReadOnlyShareCardHeaderTitle
          {...this.props}
          onDone={submit}
          onCancel={null}
          onSubmit={null}
          onClose={null}
          isSubmitDisabled={false}
        />
      );
    }

    return (
      <ShareCardHeaderTitle
        {...this.props}
        onCancel={isEditingTitle ? this.cancelTitleChange : null}
        onDone={isEditingTitle ? null : submit}
        onSubmit={isEditingTitle ? this.finishEditingTitle : null}
        onClose={null}
        isSubmitDisabled={this.isInvalid()}
      >
        <AppNavInputTitle
          value={title}
          onChange={this.updateTitle}
          onFocus={this.onFocus}
          maxLength={35}
        />
      </ShareCardHeaderTitle>
    );
  }
}


interface ShareCardHeaderNoTitleProps {
  cssColor: string;
  isEditingTitle: boolean;
  onDone(): void;
  onSubmit(): void;
  cancelChange(): void;
}

export const ShareCardHeaderNoTitle = (props: ShareCardHeaderNoTitleProps) => {
  const { cssColor, isEditingTitle, onDone, onSubmit, cancelChange } = props;
  const textColor = 'gray';

  const onCancel = isEditingTitle ? cancelChange : null;
  const onFinish = isEditingTitle ? null : onDone;
  const onSubmitChanges = isEditingTitle ? onSubmit : null;
  return (
    <ShareCardHeader style={{ backgroundColor: cssColor, minHeight: '100px' }}>
      <nav style={{ height: 56, marginLeft: 16, marginRight: 16, display: 'flex', justifyContent: 'space-between' }}>
        <LeftControl onCancel={onCancel} color={textColor} isDisabled={false} />

        <RightControl onClose={null} onDone={onFinish} onSubmit={onSubmitChanges} color={textColor} isDisabled={false} />
      </nav>

    </ShareCardHeader>
  );
};

export default ShareCardHeaderTitle;
